import type { Tabela } from '@/shared/format/exportar';

const fmtMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const fmtDataHora = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

export type Orientacao = 'retrato' | 'paisagem';

/**
 * Aplica o tamanho da folha na hora de imprimir e devolve a função que
 * desfaz. Vai por `@page` global (e não por página nomeada) porque durante
 * a impressão o único conteúdo visível é o portal — e `size: A4 landscape`
 * global é o que tem suporte garantido tanto no Chromium do Electron quanto
 * no navegador.
 *
 * A margem daqui é a que vale em todas as folhas, mas ela some quando o
 * usuário escolhe "Margens: Nenhuma" no diálogo do navegador (a escolha do
 * diálogo ganha do CSS, e o Chrome lembra a última). Por isso é menor do que
 * a margem final: o documento carrega um respiro próprio em
 * `.impressao-relatorio` (ver global.css), que nenhuma opção do diálogo
 * consegue derrubar.
 */
export function aplicarPaginaA4(orientacao: Orientacao): () => void {
  const style = document.createElement('style');
  style.setAttribute('data-print-pagesize', 'relatorio-a4');
  style.textContent =
    orientacao === 'paisagem'
      ? '@page { size: A4 landscape; margin: 7mm 7mm 9mm; }'
      : '@page { size: A4 portrait; margin: 9mm 7mm 11mm; }';
  document.head.appendChild(style);
  return () => {
    if (style.parentNode) style.parentNode.removeChild(style);
  };
}

type Props = {
  nomeEmpresa: string;
  titulo: string;
  subtitulo: string;
  emitidoEm: Date;
  tabelas: Tabela[];
};

/**
 * Folha A4 do relatório: só o que precisa sair no papel — cabeçalho da
 * oficina, o que foi filtrado e as tabelas de dados. Nada de botões, busca,
 * paginação ou badge de sincronização: o documento é montado a partir das
 * mesmas `Tabela[]` que alimentam o CSV, não de um print da tela.
 */
export function RelatorioImpressaoA4({
  nomeEmpresa,
  titulo,
  subtitulo,
  emitidoEm,
  tabelas,
}: Props) {
  return (
    <div className="impressao-a4 impressao-relatorio">
      <header className="impressao-a4__cabecalho">
        <div>
          <h1 className="impressao-a4__empresa">{nomeEmpresa}</h1>
          <div className="impressao-a4__sub">Relatório — {titulo}</div>
        </div>
        <div className="impressao-a4__num">
          <div className="impressao-a4__num-rotulo">Emitido em</div>
          <div className="impressao-a4__num-valor">
            {fmtDataHora.format(emitidoEm)}
          </div>
        </div>
      </header>

      {subtitulo && <p className="impressao-relatorio__filtros">{subtitulo}</p>}

      {tabelas.map((t, i) => (
        <TabelaImpressa key={`${t.titulo}-${i}`} tabela={t} />
      ))}
    </div>
  );
}

function TabelaImpressa({ tabela }: { tabela: Tabela }) {
  const moeda = new Set(tabela.colunasMoeda ?? []);
  const numero = new Set(tabela.colunasNumero ?? []);

  const classeCol = (i: number) =>
    moeda.has(i) || numero.has(i) ? 'impressao-a4__num-col' : undefined;

  const formatar = (valor: string | number, i: number): string => {
    if (valor == null || valor === '') return '';
    if (!moeda.has(i)) return String(valor);
    const n = typeof valor === 'number' ? valor : Number(valor);
    return Number.isFinite(n) ? fmtMoeda.format(n) : String(valor);
  };

  return (
    <section className="impressao-relatorio__secao">
      <h2 className="impressao-a4__secao-titulo">{tabela.titulo}</h2>
      {tabela.linhas.length === 0 ? (
        <p className="impressao-relatorio__vazio">Nenhum registro no período.</p>
      ) : (
        <table className="impressao-a4__tabela">
          <thead>
            <tr>
              {tabela.cabecalho.map((c, i) => (
                <th key={`${c}-${i}`} className={classeCol(i)}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabela.linhas.map((linha, li) => (
              <tr key={li}>
                {linha.map((celula, ci) => (
                  <td key={ci} className={classeCol(ci)}>
                    {formatar(celula, ci)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {tabela.rodape && (
            <tfoot>
              <tr>
                {tabela.rodape.map((celula, ci) => (
                  <td key={ci} className={classeCol(ci)}>
                    {formatar(celula, ci)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </section>
  );
}
